import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  MessageSquare, 
  Calendar, 
  LogOut,
  Users,
  CheckCircle2,
  Clock,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');

  useEffect(() => {
    const email = localStorage.getItem('admin_email');
    setAdminEmail(email);
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_email');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const statsCards = stats ? [
    {
      title: 'Kontaktanfragen',
      value: stats.total_contacts,
      icon: MessageSquare,
      color: 'bg-blue-500',
      trend: `+${stats.recent_contacts} diese Woche`
    }
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_a2b-facility/artifacts/4frauqel_AtoBObjektservice.png" 
                alt="AtoB Objektservice" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-2xl font-bold text-atob-blue">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">{adminEmail}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="text-red-600 border-red-600 hover:bg-red-50"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Abmelden
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-atob-blue text-white">
        <div className="container mx-auto px-4">
          <nav className="flex space-x-8 py-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-white/20 font-semibold"
            >
              <LayoutDashboard className="h-5 w-5" />
              <span>Dashboard</span>
            </button>
            <button
              onClick={() => navigate('/admin/contacts')}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <MessageSquare className="h-5 w-5" />
              <span>Kontakte</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-atob-orange"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statsCards.map((stat, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`${stat.color} text-white p-3 rounded-lg`}>
                        <stat.icon className="h-6 w-6" />
                      </div>
                      <TrendingUp className="h-5 w-5 text-green-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {stat.value}
                    </h3>
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      {stat.title}
                    </p>
                    <p className="text-xs text-gray-500">{stat.trend}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Schnellzugriff</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <Button
                    onClick={() => navigate('/admin/contacts')}
                    className="bg-atob-blue hover:bg-atob-blue/90 text-white py-6"
                  >
                    <MessageSquare className="mr-2 h-5 w-5" />
                    Kontaktanfragen verwalten
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="mt-8 bg-gradient-to-r from-atob-blue to-atob-blue/90 text-white">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <Users className="h-8 w-8 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Willkommen im Admin-Dashboard!</h3>
                    <p className="text-gray-100">
                      Hier können Sie alle Kontaktanfragen und Buchungen einsehen und verwalten. 
                      Nutzen Sie die Navigation oben, um zwischen den verschiedenen Bereichen zu wechseln.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};
